import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import { useStore } from "@/store/store";
import { api } from "../api";
import { useRealtimeStream } from "@uavity-core/api-client/hooks/use-realtime-stream";
import type {
  SseConnectionState,
  UpdateEvent,
} from "@uavity-core/api-client";
import { useRealtimeOrganisation } from "./useRealtimeOrganisation";
import {
  updateEventToAlertPayloads,
  updateEventToDetection,
  updateEventToGeofenceViolationPayloads,
} from "./update-event-adapter";
import {
  ConnectionStats,
  MessageType,
  DetectionPayload,
  AlertPayload,
  GeofenceViolationPayload,
} from "./types";

/** Alert freshness window - only show alerts created within this window (ms) */
const ALERT_FRESHNESS_MS = 30000;
/**
 * Bound browser SSE fanout while retaining every scope instead of truncating
 * it. These are HTTP connections per direct organisation membership, not
 * RabbitMQ Streams; unused hook slots do not connect.
 */
export const MAX_REALTIME_ORGANISATIONS = 8;

function aggregateStreamState(states: SseConnectionState[]): SseConnectionState {
  if (states.some((state) => state === "connected")) return "connected";
  if (states.some((state) => state === "connecting")) return "connecting";
  if (states.some((state) => state === "reconnecting")) return "reconnecting";
  if (states.some((state) => state === "error")) return "error";
  return "disconnected";
}

/**
 * Context value for the durable realtime Stream connection.
 */
interface RealtimeContextValue {
  /** Current connection statistics */
  connectionStats: ConnectionStats;
  /** Whether the SSE stream is connected */
  isConnected: boolean;
  /** Subscribe to additional event types */
  subscribe: (eventTypes: MessageType[], sensorIds?: string[]) => void;
  /** Unsubscribe from event types */
  unsubscribe: (eventTypes: MessageType[]) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(
  undefined
);

interface RealtimeProviderProps {
  children: ReactNode;
}

/**
 * Validates that a detection payload has the required fields.
 */
function isValidDetection(payload: DetectionPayload): boolean {
  const d = payload.detection;
  return (
    typeof d.id === "string" &&
    typeof d.sensor_id === "string" &&
    typeof d.detection_time === "string" &&
    typeof d.latitude === "number" &&
    Number.isFinite(d.latitude) &&
    typeof d.longitude === "number" &&
    Number.isFinite(d.longitude) &&
    d.location_valid === true &&
    typeof d.detection_type === "string" &&
    typeof d.received_at === "string"
  );
}

/**
 * Checks if an alert is fresh enough to display.
 */
function isAlertFresh(timestamp: string): boolean {
  try {
    const alertTime = new Date(timestamp).getTime();
    return Date.now() - alertTime < ALERT_FRESHNESS_MS;
  } catch {
    return false;
  }
}

/**
 * Provider component for durable real-time Stream functionality.
 * 
 * Handles:
 * - authenticated SSE connection with opaque cursor replay
 * - Detection processing and store updates
 * - Alert notifications via toast
 * - Geofence violation handling
 * 
 * @example
 * ```tsx
 * <RealtimeProvider>
 *   <App />
 * </RealtimeProvider>
 * ```
 */
export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { getToken } = useAuth();
  const { data: organisations } = useRealtimeOrganisation();
  const organisationScopeError =
    organisations && organisations.length > MAX_REALTIME_ORGANISATIONS
      ? `Realtime is unavailable for accounts with more than ${MAX_REALTIME_ORGANISATIONS} organisations; choose a smaller organisation scope.`
      : null;
  const streamOrganisations = useMemo(
    () =>
      organisationScopeError
        ? []
        : (organisations?.map((organisation) => organisation.slug) ?? []),
    [organisations, organisationScopeError],
  );
  const streamScopeKey = streamOrganisations.join("|");

  // Store actions
  const addDetection = useStore((s) => s.addDetection);
  const addDrone = useStore((s) => s.addDrone);

  // SSE events carry the contributing sensor. Keep that small amount of
  // connection metadata so existing status HUD consumers retain their shape.
  const [sensorIds, setSensorIds] = useState<string[]>([]);
  const sensorIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    sensorIdsRef.current.clear();
    setSensorIds([]);
  }, [streamScopeKey]);

  /**
   * Handle detection events from the server.
   */
  const handleDetection = useCallback(
    (payload: DetectionPayload) => {
      if (!isValidDetection(payload)) {
        return;
      }

      const detection = payload.detection;

      // Update store
      addDetection(detection);

      if (detection.drone_identifier) {
        addDrone(detection.drone_identifier);
      }
    },
    [addDetection, addDrone]
  );

  /**
   * Handle alert events from the server.
   */
  const handleAlert = useCallback((payload: AlertPayload) => {
    // Only show fresh alerts
    if (!isAlertFresh(payload.timestamp)) {
      return;
    }

    // Show toast based on severity
    switch (payload.severity) {
      case "critical":
        toast.error(payload.title, {
          description: payload.message,
          duration: 10000,
        });
        break;
      case "warning":
        toast.warning(payload.title, {
          description: payload.message,
          duration: 7000,
        });
        break;
      case "info":
      default:
        toast.info(payload.title, {
          description: payload.message,
          duration: 5000,
        });
    }
  }, []);

  /**
   * Handle geofence violation events from the server.
   */
  const handleGeofenceViolation = useCallback(
    (payload: GeofenceViolationPayload) => {
      // Show notification
      toast.warning(`Geofence Violation: ${payload.geofence_name}`, {
        description: `Drone ${payload.detection.drone_identifier || "Unknown"} ${payload.violation_type} detected`,
        duration: 8000,
      });

      // Also add the detection to the store
      if (payload.detection) {
        addDetection(payload.detection);
        if (payload.detection.drone_identifier) {
          addDrone(payload.detection.drone_identifier);
        }
      }
    },
    [addDetection, addDrone]
  );

  const handleStreamEvent = useCallback(
    (event: UpdateEvent) => {
      if (event.sensor_id && !sensorIdsRef.current.has(event.sensor_id)) {
        sensorIdsRef.current.add(event.sensor_id);
        setSensorIds(Array.from(sensorIdsRef.current).sort());
      }

      const detection = updateEventToDetection(event);
      if (detection) {
        const payload: DetectionPayload = {
          detection,
          sensor_id: event.sensor_id ?? detection.sensor_id,
        };
        if (isValidDetection(payload)) {
          handleDetection(payload);
        }
      }

      for (const alert of updateEventToAlertPayloads(event)) {
        handleAlert(alert);
      }
      for (const violation of updateEventToGeofenceViolationPayloads(event)) {
        handleGeofenceViolation(violation);
      }
    },
    [handleAlert, handleDetection, handleGeofenceViolation],
  );

  const getAuthHeaders = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      throw new Error("Authentication required");
    }
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const streamCallbacks = useMemo(() => ({
    onEvent: handleStreamEvent,
    getAuthHeaders,
  }), [getAuthHeaders, handleStreamEvent]);

  // Keep hook order static while allowing every membership to receive its own
  // generation-bound cursor. Accounts above the explicit bound fail closed
  // rather than silently dropping an organisation's live feed.
  const stream0 = useRealtimeStream(
    api,
    streamOrganisations[0] ?? null,
    streamCallbacks,
  );
  const stream1 = useRealtimeStream(
    api,
    streamOrganisations[1] ?? null,
    streamCallbacks,
  );
  const stream2 = useRealtimeStream(
    api,
    streamOrganisations[2] ?? null,
    streamCallbacks,
  );
  const stream3 = useRealtimeStream(
    api,
    streamOrganisations[3] ?? null,
    streamCallbacks,
  );
  const stream4 = useRealtimeStream(
    api,
    streamOrganisations[4] ?? null,
    streamCallbacks,
  );
  const stream5 = useRealtimeStream(
    api,
    streamOrganisations[5] ?? null,
    streamCallbacks,
  );
  const stream6 = useRealtimeStream(
    api,
    streamOrganisations[6] ?? null,
    streamCallbacks,
  );
  const stream7 = useRealtimeStream(
    api,
    streamOrganisations[7] ?? null,
    streamCallbacks,
  );
  const streams = [
    stream0,
    stream1,
    stream2,
    stream3,
    stream4,
    stream5,
    stream6,
    stream7,
  ];
  const activeStreams = streams.slice(0, streamOrganisations.length);
  const activeStreamStats = activeStreams.map((stream) => stream.stats);

  const connectionStats = useMemo<ConnectionStats>(
    () => ({
      state: organisationScopeError
        ? "error"
        : aggregateStreamState(
            activeStreamStats.map((stats) => stats.state as SseConnectionState),
          ),
      connectedAt:
        activeStreamStats.find((stats) => stats.connectedAt != null)
          ?.connectedAt ?? null,
      reconnectAttempts: activeStreamStats.reduce(
        (max, stats) => Math.max(max, stats.reconnectAttempts),
        0,
      ),
      lastError:
        organisationScopeError ??
        activeStreamStats.find((stats) => stats.lastError != null)?.lastError ??
        null,
      sensorCount: sensorIds.length,
      sensorIds,
    }),
    [activeStreamStats, organisationScopeError, sensorIds],
  );

  const subscribe = useCallback(
    (eventTypes: MessageType[], sensorIds?: string[]) => {
      // Stream visibility is determined by the authenticated organisation
      // scope. It has no client-side subscription messages.
      void eventTypes;
      void sensorIds;
    },
    [],
  );
  const unsubscribe = useCallback((eventTypes: MessageType[]) => {
    // Stream visibility is determined by the authenticated organisation
    // scope. It has no client-side subscription messages.
    void eventTypes;
  }, []);

  const contextValue: RealtimeContextValue = {
    connectionStats,
    isConnected:
      !organisationScopeError && activeStreams.some((stream) => stream.isConnected),
    subscribe,
    unsubscribe,
  };

  return (
    <RealtimeContext.Provider value={contextValue}>
      {children}
    </RealtimeContext.Provider>
  );
}

/**
 * Hook to access the realtime Stream context.
 * Must be used within a RealtimeProvider.
 * 
 * @example
 * ```tsx
 * const { isConnected, connectionStats } = useRealtimeContext();
 * ```
 */
export function useRealtimeContext() {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error(
      "useRealtimeContext must be used within a RealtimeProvider"
    );
  }
  return context;
}

/**
 * @deprecated Use RealtimeProvider and useRealtimeContext instead. The
 * provider now uses SSE; this name remains for source compatibility only.
 */
export const WebSocketProvider = RealtimeProvider;

/**
 * @deprecated Use useRealtimeContext instead
 */
export const useSocket = useRealtimeContext;
